// src/api.js
//
// All backend calls. The browser session lives in an HttpOnly cookie set by the
// backend after the user accepted the cookie notice; every request sends it
// automatically (same origin). State-changing requests carry the X-LJD-Request
// header the backend uses as its CSRF check.

import axios from "axios";

// - In single-container deployment: defaults to same-origin "/api".
// - In dev with separate backend: set REACT_APP_API_BASE=http://localhost:8000/api
const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json", "X-LJD-Request": "1" },
});

// Optional login gate (REACT_APP_LOGIN_NEEDED): JWT stored by the login page.
function getAuthToken() {
  try {
    const raw = window.localStorage.getItem("t4r_auth");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || parsed?.access_token || null;
  } catch {
    return null;
  }
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Error with the backend's error contract: {code, message, field_errors, retry_after, runs_left}. */
export class ApiError extends Error {
  constructor({ status, code, message, fieldErrors, retryAfter, runsLeft, current, neededSteps, network }) {
    super(message || "Something went wrong.");
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || null;
    this.retryAfter = retryAfter ?? null;
    this.runsLeft = runsLeft ?? null;
    this.current = current || null;
    this.neededSteps = neededSteps || null;
    this.network = !!network;
  }
}

function toApiError(err) {
  if (!err.response) {
    return new ApiError({
      status: 0,
      code: "network",
      message: "You appear to be offline. We will retry automatically.",
      network: true,
    });
  }
  const { status, data } = err.response;
  const body = data && typeof data === "object" ? data : {};
  const detail = body.detail && typeof body.detail === "object" ? body.detail : body;
  return new ApiError({
    status,
    code: detail.code || `http_${status}`,
    message:
      detail.message ||
      (typeof body.detail === "string" ? body.detail : null) ||
      `Request failed (${status}).`,
    fieldErrors: detail.field_errors,
    retryAfter: detail.retry_after,
    runsLeft: detail.runs_left,
    current: detail.current,
    neededSteps: detail.needed_steps,
  });
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && getAuthToken()) {
      // Login gate: emit a custom event so the UI can show a re-login prompt.
      window.dispatchEvent(new CustomEvent("t4r-auth-expired"));
    }
    return Promise.reject(toApiError(error));
  }
);

const data = (p) => p.then((r) => r.data);

// ------------------ Auth (optional login gate) ------------------
export const login = (email, password) => data(api.post("auth/login", { email, password }));

// ------------------ Public ------------------
export const getHealth = () => data(api.get("health"));
export const getConfig = () => data(api.get("config"));
export const getVersion = () => data(api.get("version")); // {version, changelog}
export const getCatalog = () => data(api.get("catalog")); // { ok, items: [...] }
export const getCatalogEntry = (id) => data(api.get(`catalog/${encodeURIComponent(id)}`));
export const getChatbotPrompt = (step) => data(api.get(`schema/steps/${step}/prompt`));

// ------------------ Session ------------------
/** Creates the session cookie. Only call after the user accepted the cookie notice. */
export const startSession = ({ resumeToken } = {}) =>
  data(api.post("session", { consent: true, resume_token: resumeToken || null }));
export const getSession = () => data(api.get("session"));
export const getSessionStatus = () => data(api.get("session/status")); // {active}
export const deleteSession = () => data(api.delete("session"));
export const forgetSessionCookie = () => data(api.post("session/logout"));
/** This browser's journeys (current first): {journeys: [{ref, current, created_at, expires_at, steps_completed, ...}]} */
export const listJourneys = () => data(api.get("session/journeys"));
export const switchJourney = (ref) => data(api.post("session/journeys/switch", { ref }));

export const getStep = (step) => data(api.get(`session/steps/${step}`));
export const saveStep = (step, stepData, version) =>
  data(api.put(`session/steps/${step}`, { data: stepData, version }));
export const confirmStep = (step, version) =>
  data(api.post(`session/steps/${step}/confirm`, { version: version ?? null }));
/** Runs the tool's own AI for step 5 or 7. `force` regenerates (counts as a run). */
export const runStep = (step, force = false) =>
  data(api.post(`session/steps/${step}/run`, { force }));
export const importStep = (step, text) => data(api.post(`session/steps/${step}/import`, { text }));

export const createResumeLink = () => data(api.post("session/resume-link"));
export const createAssistantCode = () => data(api.post("session/assistant-code"));
export const revokeAssistantCodes = () => data(api.delete("session/assistant-code"));

// ------------------ Bot check (ALTCHA, only when enabled) ------------------
export const BOTCHECK_CHALLENGE_URL = `${API_BASE}/botcheck/challenge`;
export const verifyHuman = (payload) => data(api.post("session/verify-human", { payload }));

export { API_BASE };
